import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Check, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

// Heuristic bucketing for the demo. Real categories would be configured in
// the admin tab; this just illustrates the layout.
function categorise(name = "") {
  const n = name.toLowerCase();
  if (/braces|invisalign|ortho|aligner/.test(n)) return "Orthodontics";
  if (/whiten|veneer|cosmetic|smile makeover|bonding/.test(n)) return "Cosmetic";
  if (/implant/.test(n)) return "Implants";
  if (/child|kid|paedi|paediatric|pediatric/.test(n)) return "Children's";
  if (/hygien|cleaning|scale|polish|fluoride/.test(n)) return "Hygiene";
  if (/extract|root|crown|filling|bridge|denture|wisdom/.test(n)) return "General Dentistry";
  if (/emergency|urgent|same.?day|pain/.test(n)) return "Emergency";
  return "Other Treatments";
}

const CATEGORY_ORDER = [
  "Orthodontics", "Cosmetic", "Implants", "Hygiene",
  "General Dentistry", "Children's", "Emergency", "Other Treatments",
];

const CATEGORY_ICONS = {
  Orthodontics: "🦷", Cosmetic: "✨", Implants: "🦴", Hygiene: "🧼",
  "General Dentistry": "🩺", "Children's": "🧒", Emergency: "🚑",
  "Other Treatments": "📋",
};

const fmtPrice = (s) =>
  (s.price || 0) > 0 ? `€${Number(s.price).toFixed(Number.isInteger(s.price) ? 0 : 2)}` : "Free";

// ─────────────────────────────────────────────────────────────────────
// Layout 1: Grouped Accordion
// ─────────────────────────────────────────────────────────────────────
function LayoutAccordion({ services }) {
  const grouped = useMemo(() => {
    const map = {};
    services.forEach((s) => {
      const c = categorise(s.name);
      (map[c] = map[c] || []).push(s);
    });
    return CATEGORY_ORDER.filter((c) => map[c]).map((c) => ({ category: c, items: map[c] }));
  }, [services]);
  const [open, setOpen] = useState(null);
  const [picked, setPicked] = useState(null);
  return (
    <div className="space-y-2">
      {grouped.map(({ category, items }) => {
        const isOpen = open === category;
        return (
          <div key={category} className="border-2 border-[#E5E5E5] rounded-lg overflow-hidden bg-white">
            <button
              onClick={() => setOpen(isOpen ? null : category)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#F8F9FB]"
            >
              <span className="flex items-center gap-3">
                <span className="text-xl">{CATEGORY_ICONS[category]}</span>
                <span className="font-bold text-[#1A1A1A]">{category}</span>
                <span className="text-xs text-[#5C5C5C]">({items.length})</span>
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="border-t border-[#E5E5E5] divide-y divide-[#F0F0F0]">
                {items.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setPicked(s)}
                    className={`w-full px-4 py-3 flex items-center justify-between hover:bg-[#F0F6FF] text-left ${picked?.id === s.id ? "bg-[#F0F6FF]" : ""}`}
                  >
                    <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
                    <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">{fmtPrice(s)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {picked && <p className="text-sm text-[#5C5C5C] mt-3">Picked: <strong className="text-[#1A1A1A]">{picked.name}</strong> · {fmtPrice(picked)}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout 2: Search Combobox
// ─────────────────────────────────────────────────────────────────────
function LayoutSearchCombobox({ services }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const filtered = services.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  return (
    <div>
      <div className="relative mb-2">
        <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search treatments — try 'invisalign', 'whitening', 'check'…"
          className="w-full bg-white border-2 border-[#C9C9C9] rounded-lg pl-10 pr-3 py-3 text-sm focus:border-[#0466ff] focus:outline-none"
        />
      </div>
      <div className="border-2 border-[#E5E5E5] rounded-lg max-h-72 overflow-y-auto bg-white divide-y divide-[#F0F0F0]">
        {filtered.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-[#5C5C5C]">No matches. Try a different keyword.</div>
        ) : filtered.map((s) => (
          <button
            key={s.id}
            onClick={() => setPicked(s)}
            className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#F0F6FF] ${picked?.id === s.id ? "bg-[#F0F6FF]" : ""}`}
          >
            <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
            <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">{fmtPrice(s)}</span>
          </button>
        ))}
      </div>
      {picked && <p className="text-sm text-[#5C5C5C] mt-3">Picked: <strong className="text-[#1A1A1A]">{picked.name}</strong> · {fmtPrice(picked)}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout 3: Hero Cards + See All
// ─────────────────────────────────────────────────────────────────────
function LayoutHeroCards({ services }) {
  // Show first 5 paid services as "popular" + a free consult; replace with
  // real most-popular IDs in production.
  const popular = services.filter((s) => (s.price || 0) > 0).slice(0, 5);
  const [showAll, setShowAll] = useState(false);
  const [picked, setPicked] = useState(null);
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {popular.map((s) => {
          const sel = picked?.id === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setPicked(s)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${sel ? "border-[#0466ff] bg-[#F0F6FF]" : "border-[#E5E5E5] bg-white hover:border-[#B3D1FF]"}`}
            >
              <div className="text-2xl mb-2">{CATEGORY_ICONS[categorise(s.name)] || "🦷"}</div>
              <div className="text-sm font-bold text-[#1A1A1A] mb-1 leading-tight">{s.name}</div>
              <div className="text-sm text-[#0466ff] font-semibold">{fmtPrice(s)}</div>
            </button>
          );
        })}
      </div>
      <button
        onClick={() => setShowAll((v) => !v)}
        className="w-full text-center text-sm font-semibold text-[#0466ff] hover:underline py-2"
      >
        {showAll ? "Hide" : `See all ${services.length} treatments →`}
      </button>
      {showAll && (
        <div className="border-2 border-[#E5E5E5] rounded-lg mt-2 max-h-72 overflow-y-auto bg-white divide-y divide-[#F0F0F0]">
          {services.map((s) => (
            <button
              key={s.id}
              onClick={() => setPicked(s)}
              className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#F0F6FF] ${picked?.id === s.id ? "bg-[#F0F6FF]" : ""}`}
            >
              <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
              <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">{fmtPrice(s)}</span>
            </button>
          ))}
        </div>
      )}
      {picked && <p className="text-sm text-[#5C5C5C] mt-3">Picked: <strong className="text-[#1A1A1A]">{picked.name}</strong> · {fmtPrice(picked)}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout 4: Two-step picker (Category → Service)
// ─────────────────────────────────────────────────────────────────────
function LayoutTwoStep({ services }) {
  const grouped = useMemo(() => {
    const map = {};
    services.forEach((s) => {
      const c = categorise(s.name);
      (map[c] = map[c] || []).push(s);
    });
    return CATEGORY_ORDER.filter((c) => map[c]).map((c) => ({ category: c, items: map[c] }));
  }, [services]);
  const [cat, setCat] = useState(null);
  const [picked, setPicked] = useState(null);
  const items = cat ? grouped.find((g) => g.category === cat)?.items || [] : [];
  return (
    <div>
      {!cat ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {grouped.map(({ category, items }) => (
            <button
              key={category}
              onClick={() => setCat(category)}
              className="p-4 rounded-xl border-2 border-[#E5E5E5] bg-white text-left hover:border-[#0466ff] transition-all"
            >
              <div className="text-2xl mb-2">{CATEGORY_ICONS[category]}</div>
              <div className="text-sm font-bold text-[#1A1A1A]">{category}</div>
              <div className="text-xs text-[#5C5C5C] mt-0.5">{items.length} treatment{items.length !== 1 ? "s" : ""}</div>
            </button>
          ))}
        </div>
      ) : (
        <div>
          <button onClick={() => { setCat(null); setPicked(null); }} className="text-sm text-[#0466ff] font-semibold mb-3 inline-flex items-center gap-1">
            ← All categories
          </button>
          <div className="border-2 border-[#E5E5E5] rounded-lg bg-white divide-y divide-[#F0F0F0]">
            {items.map((s) => (
              <button
                key={s.id}
                onClick={() => setPicked(s)}
                className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#F0F6FF] ${picked?.id === s.id ? "bg-[#F0F6FF]" : ""}`}
              >
                <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
                <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">{fmtPrice(s)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {picked && <p className="text-sm text-[#5C5C5C] mt-3">Picked: <strong className="text-[#1A1A1A]">{picked.name}</strong> · {fmtPrice(picked)}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout 5: Tabbed interface
// ─────────────────────────────────────────────────────────────────────
function LayoutTabs({ services }) {
  const grouped = useMemo(() => {
    const map = {};
    services.forEach((s) => { const c = categorise(s.name); (map[c] = map[c] || []).push(s); });
    return CATEGORY_ORDER.filter((c) => map[c]).map((c) => ({ category: c, items: map[c] }));
  }, [services]);
  const [tab, setTab] = useState(null);
  const [picked, setPicked] = useState(null);
  if (tab === null && grouped[0]) {
    // Initial tab — pick the first category once data arrives
    // (cheap, runs in render but only until tab is set)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    setTimeout(() => setTab(grouped[0].category), 0);
  }
  const items = grouped.find((g) => g.category === tab)?.items || [];
  return (
    <div>
      <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1 mb-3 border-b border-[#E5E5E5]">
        {grouped.map(({ category, items }) => (
          <button
            key={category}
            onClick={() => setTab(category)}
            className={`px-3 py-2 text-sm font-semibold whitespace-nowrap rounded-t-md border-b-2 transition-colors ${tab === category ? "border-[#0466ff] text-[#0466ff] bg-[#F0F6FF]" : "border-transparent text-[#5C5C5C] hover:text-[#1A1A1A]"}`}
          >
            <span className="mr-1.5">{CATEGORY_ICONS[category]}</span>{category}
            <span className="ml-1 text-[10px] opacity-60">({items.length})</span>
          </button>
        ))}
      </div>
      <div className="border-2 border-[#E5E5E5] rounded-lg bg-white divide-y divide-[#F0F0F0]">
        {items.map((s) => (
          <button key={s.id} onClick={() => setPicked(s)} className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#F0F6FF] ${picked?.id === s.id ? "bg-[#F0F6FF]" : ""}`}>
            <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
            <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">{fmtPrice(s)}</span>
          </button>
        ))}
      </div>
      {picked && <p className="text-sm text-[#5C5C5C] mt-3">Picked: <strong className="text-[#1A1A1A]">{picked.name}</strong> · {fmtPrice(picked)}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout 6: Chip filtering (multi-select)
// ─────────────────────────────────────────────────────────────────────
function LayoutChips({ services }) {
  const [active, setActive] = useState(new Set());
  const [picked, setPicked] = useState(null);
  const chips = ["All", ...CATEGORY_ORDER.filter((c) => services.some((s) => categorise(s.name) === c))];
  const toggle = (chip) => {
    const next = new Set(active);
    if (chip === "All") { next.clear(); setActive(next); return; }
    next.has(chip) ? next.delete(chip) : next.add(chip);
    setActive(next);
  };
  const filtered = active.size === 0 ? services : services.filter((s) => active.has(categorise(s.name)));
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {chips.map((c) => {
          const isAll = c === "All";
          const on = isAll ? active.size === 0 : active.has(c);
          return (
            <button key={c} onClick={() => toggle(c)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${on ? "border-[#0466ff] bg-[#0466ff] text-white" : "border-[#C9C9C9] bg-white text-[#1A1A1A] hover:border-[#0466ff]"}`}>
              {!isAll && <span className="mr-1">{CATEGORY_ICONS[c]}</span>}{c}
            </button>
          );
        })}
      </div>
      <div className="border-2 border-[#E5E5E5] rounded-lg bg-white divide-y divide-[#F0F0F0] max-h-72 overflow-y-auto">
        {filtered.map((s) => (
          <button key={s.id} onClick={() => setPicked(s)} className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#F0F6FF] ${picked?.id === s.id ? "bg-[#F0F6FF]" : ""}`}>
            <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
            <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">{fmtPrice(s)}</span>
          </button>
        ))}
      </div>
      {picked && <p className="text-sm text-[#5C5C5C] mt-3">Picked: <strong className="text-[#1A1A1A]">{picked.name}</strong> · {fmtPrice(picked)}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout 7: Guided Q&A wizard
// ─────────────────────────────────────────────────────────────────────
function LayoutGuided({ services }) {
  const intents = [
    { label: "I want straighter teeth", icon: "😬", cats: ["Orthodontics"] },
    { label: "I want a whiter smile", icon: "✨", cats: ["Cosmetic"] },
    { label: "I'm in pain right now", icon: "🚑", cats: ["Emergency", "General Dentistry"] },
    { label: "Routine check-up / clean", icon: "🧼", cats: ["Hygiene", "General Dentistry"] },
    { label: "Missing a tooth", icon: "🦴", cats: ["Implants"] },
    { label: "Something for my child", icon: "🧒", cats: ["Children's"] },
    { label: "Something else", icon: "📋", cats: CATEGORY_ORDER },
  ];
  const [intent, setIntent] = useState(null);
  const [picked, setPicked] = useState(null);
  const matches = intent ? services.filter((s) => intent.cats.includes(categorise(s.name))) : [];
  return (
    <div>
      {!intent ? (
        <>
          <p className="text-sm font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: "Manrope" }}>What can we help with?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {intents.map((it) => (
              <button key={it.label} onClick={() => setIntent(it)} className="p-3 rounded-lg border-2 border-[#E5E5E5] bg-white text-left hover:border-[#0466ff] hover:bg-[#F0F6FF] flex items-center gap-3 transition-colors">
                <span className="text-2xl">{it.icon}</span>
                <span className="text-sm font-semibold text-[#1A1A1A]">{it.label}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div>
          <button onClick={() => { setIntent(null); setPicked(null); }} className="text-sm text-[#0466ff] font-semibold mb-3 inline-flex items-center gap-1">← Back</button>
          <p className="text-sm font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: "Manrope" }}>Treatments for &quot;{intent.label}&quot;</p>
          {matches.length === 0 ? (
            <p className="text-sm text-[#5C5C5C] italic">No treatments configured yet for this need. Please call us on 01 667 8524.</p>
          ) : (
            <div className="border-2 border-[#E5E5E5] rounded-lg bg-white divide-y divide-[#F0F0F0]">
              {matches.map((s) => (
                <button key={s.id} onClick={() => setPicked(s)} className={`w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#F0F6FF] ${picked?.id === s.id ? "bg-[#F0F6FF]" : ""}`}>
                  <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
                  <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">{fmtPrice(s)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {picked && <p className="text-sm text-[#5C5C5C] mt-3">Picked: <strong className="text-[#1A1A1A]">{picked.name}</strong> · {fmtPrice(picked)}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout 8: Sticky search + grouped list
// ─────────────────────────────────────────────────────────────────────
function LayoutSearchGrouped({ services }) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const grouped = useMemo(() => {
    const map = {};
    services.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())).forEach((s) => {
      const c = categorise(s.name); (map[c] = map[c] || []).push(s);
    });
    return CATEGORY_ORDER.filter((c) => map[c]).map((c) => ({ category: c, items: map[c] }));
  }, [services, q]);
  return (
    <div>
      <div className="sticky top-0 bg-white z-10 pb-3 -mx-1 px-1 mb-3 border-b border-[#E5E5E5]">
        <div className="relative">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search treatments — type to filter…"
            className="w-full bg-white border-2 border-[#C9C9C9] rounded-lg pl-10 pr-3 py-2.5 text-sm focus:border-[#0466ff] focus:outline-none"
          />
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto space-y-4">
        {grouped.length === 0 ? (
          <div className="text-sm text-[#5C5C5C] py-6 text-center">No matches. Try a different keyword.</div>
        ) : grouped.map(({ category, items }) => (
          <div key={category}>
            <div className="flex items-center gap-2 mb-1.5 px-1">
              <span className="text-base">{CATEGORY_ICONS[category]}</span>
              <h4 className="text-xs font-extrabold uppercase tracking-wider text-[#5C5C5C]" style={{ fontFamily: "Manrope" }}>{category}</h4>
            </div>
            <div className="border-2 border-[#E5E5E5] rounded-lg bg-white divide-y divide-[#F0F0F0]">
              {items.map((s) => (
                <button key={s.id} onClick={() => setPicked(s)} className={`w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-[#F0F6FF] ${picked?.id === s.id ? "bg-[#F0F6FF]" : ""}`}>
                  <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
                  <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">{fmtPrice(s)}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {picked && <p className="text-sm text-[#5C5C5C] mt-3">Picked: <strong className="text-[#1A1A1A]">{picked.name}</strong> · {fmtPrice(picked)}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Layout 9: Visual catalog (placeholder gradients — would be real photos)
// ─────────────────────────────────────────────────────────────────────
function LayoutVisualCatalog({ services }) {
  const [picked, setPicked] = useState(null);
  // Pretty gradient palette per category — stand-in until you supply real photos
  const gradient = (cat) => ({
    "Orthodontics": "linear-gradient(135deg,#0466ff,#5096ff)",
    "Cosmetic": "linear-gradient(135deg,#FF6CB2,#FFB366)",
    "Implants": "linear-gradient(135deg,#6B7280,#9CA3AF)",
    "Hygiene": "linear-gradient(135deg,#22C55E,#86EFAC)",
    "General Dentistry": "linear-gradient(135deg,#0EA5E9,#7DD3FC)",
    "Children's": "linear-gradient(135deg,#A855F7,#F0ABFC)",
    "Emergency": "linear-gradient(135deg,#EF4444,#FB923C)",
    "Other Treatments": "linear-gradient(135deg,#1F2937,#4B5563)",
  }[cat] || "linear-gradient(135deg,#0466ff,#5096ff)");
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {services.map((s) => {
          const cat = categorise(s.name);
          const sel = picked?.id === s.id;
          return (
            <button key={s.id} onClick={() => setPicked(s)} className={`group rounded-xl overflow-hidden border-2 text-left transition-all ${sel ? "border-[#0466ff] shadow-md" : "border-[#E5E5E5] hover:border-[#B3D1FF]"}`}>
              <div className="aspect-[4/3] grid place-items-center text-4xl" style={{ background: gradient(cat) }}>
                <span className="drop-shadow-sm">{CATEGORY_ICONS[cat]}</span>
              </div>
              <div className="p-3 bg-white">
                <div className="text-[11px] uppercase tracking-wider text-[#5C5C5C] font-semibold mb-1">{cat}</div>
                <div className="text-sm font-bold text-[#1A1A1A] leading-tight mb-1">{s.name}</div>
                <div className="text-sm text-[#0466ff] font-semibold">{fmtPrice(s)}</div>
              </div>
            </button>
          );
        })}
      </div>
      {picked && <p className="text-sm text-[#5C5C5C] mt-3">Picked: <strong className="text-[#1A1A1A]">{picked.name}</strong> · {fmtPrice(picked)}</p>}
      <p className="text-[11px] text-[#9CA3AF] mt-2 italic">Note: the gradient placeholders would be replaced with real before/after smile photos or treatment imagery in production.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// FLAT LIST layouts (no categorisation) — added per request
// ─────────────────────────────────────────────────────────────────────

// Layout 10: Plain alphabetical list
function LayoutFlatAlpha({ services }) {
  const list = [...services].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="border-2 border-[#C9C9C9] rounded-lg bg-white max-h-[460px] overflow-y-auto divide-y divide-[#F0F0F0]">
      {list.map((s) => (
        <div key={s.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#F2F2F2]">
          <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
          <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">
            {s.price > 0 ? `€${Number.isInteger(s.price) ? s.price : s.price.toFixed(2)}` : <span className="text-[10px] font-bold text-white bg-[#1FAD3A] px-2 py-0.5 rounded-full">FREE</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

// Layout 11: Sorted by price (cheap → expensive)
function LayoutFlatPrice({ services }) {
  const list = [...services].sort((a, b) => (a.price || 0) - (b.price || 0));
  return (
    <div className="border-2 border-[#C9C9C9] rounded-lg bg-white max-h-[460px] overflow-y-auto divide-y divide-[#F0F0F0]">
      {list.map((s) => (
        <div key={s.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#F2F2F2]">
          <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
          <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">
            {s.price > 0 ? `€${Number.isInteger(s.price) ? s.price : s.price.toFixed(2)}` : <span className="text-[10px] font-bold text-white bg-[#1FAD3A] px-2 py-0.5 rounded-full">FREE</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

// Layout 12: Sticky search + flat list (no categories)
function LayoutFlatSearch({ services }) {
  const [q, setQ] = useState("");
  const list = services.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search treatments…"
        className="ws-input w-full mb-3"
      />
      <div className="border-2 border-[#C9C9C9] rounded-lg bg-white max-h-[420px] overflow-y-auto divide-y divide-[#F0F0F0]">
        {list.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-[#5C5C5C]">No treatments match &ldquo;{q}&rdquo;.</div>
        ) : list.map((s) => (
          <div key={s.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#F2F2F2]">
            <span className="text-sm text-[#1A1A1A] pr-3">{s.name}</span>
            <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">
              {s.price > 0 ? `€${Number.isInteger(s.price) ? s.price : s.price.toFixed(2)}` : <span className="text-[10px] font-bold text-white bg-[#1FAD3A] px-2 py-0.5 rounded-full">FREE</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Layout 13: Two-column dense list (desktop), single col (mobile)
function LayoutFlatTwoCol({ services }) {
  return (
    <div className="border-2 border-[#C9C9C9] rounded-lg bg-white p-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        {services.map((s) => (
          <div key={s.id} className="px-3 py-2.5 flex items-center justify-between border-b border-[#F0F0F0] hover:bg-[#F2F2F2] rounded">
            <span className="text-sm text-[#1A1A1A] pr-2 truncate" title={s.name}>{s.name}</span>
            <span className="text-sm font-semibold text-[#0466ff] flex-shrink-0">
              {s.price > 0 ? `€${Number.isInteger(s.price) ? s.price : s.price.toFixed(2)}` : <span className="text-[10px] font-bold text-white bg-[#1FAD3A] px-2 py-0.5 rounded-full">FREE</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Layout 14: Numbered "treatment menu" (restaurant-style)
function LayoutFlatMenu({ services }) {
  return (
    <div className="bg-[#FAFAFA] border-2 border-[#1A1A1A] rounded-lg p-5 sm:p-8" style={{ fontFamily: "Manrope" }}>
      <div className="text-center mb-5">
        <h3 className="text-xl font-extrabold tracking-wider uppercase text-[#1A1A1A]">Treatments</h3>
        <div className="text-xs text-[#5C5C5C] tracking-widest uppercase mt-1">White Smile Dental</div>
        <div className="border-t border-[#1A1A1A] mt-3 mb-1" />
      </div>
      <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1">
        {services.map((s, i) => (
          <div key={s.id} className="flex items-baseline gap-2">
            <span className="text-[12px] font-bold text-[#5C5C5C] tabular-nums">{String(i + 1).padStart(2, "0")}.</span>
            <span className="text-sm text-[#1A1A1A] truncate">{s.name}</span>
            <span className="flex-1 border-b border-dotted border-[#8C8C8C] translate-y-[-2px]" />
            <span className="text-sm font-bold text-[#1A1A1A] tabular-nums">
              {s.price > 0 ? `€${Number.isInteger(s.price) ? s.price : s.price.toFixed(2)}` : "FREE"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Preview page wrapper
// ─────────────────────────────────────────────────────────────────────
const LAYOUTS = [
  { key: "accordion", title: "Layout 1 — Grouped accordion", desc: "Categories collapse and expand. Familiar 'FAQ' pattern. Scales well to 50+ services.", component: LayoutAccordion },
  { key: "search", title: "Layout 2 — Search-as-you-type combobox", desc: "Type to filter instantly. Fast for patients who know what they want; great paired with categories.", component: LayoutSearchCombobox },
  { key: "hero", title: "Layout 3 — Hero cards + 'See all'", desc: "Surface 5 highest-converting treatments upfront as visual cards. Full list one tap away.", component: LayoutHeroCards },
  { key: "twostep", title: "Layout 4 — Two-step (category → treatment)", desc: "Tap a category, then a treatment. Cleanest mobile UX. Adds one tap but reduces choice overload.", component: LayoutTwoStep },
  { key: "tabs", title: "Layout 5 — Tabbed interface", desc: "Horizontal category tabs at top, list below swaps in. Familiar pattern (Stripe pricing-style). Tabs scroll horizontally on mobile.", component: LayoutTabs },
  { key: "chips", title: "Layout 6 — Chip filtering (multi-select)", desc: "Tap chips to filter the long list by category. Multi-select supported. Patients explore by need rather than category names.", component: LayoutChips },
  { key: "guided", title: "Layout 7 — Guided Q&A ('What brings you in?')", desc: "Conversational. Hides the long list entirely. Best for patients who don't know clinical terms — also a brand differentiator.", component: LayoutGuided },
  { key: "searchgrouped", title: "Layout 8 — Sticky search + grouped list", desc: "Best of accordion + combobox. Categories visible by default, search filters live. My personal pick for 34+ items.", component: LayoutSearchGrouped },
  { key: "catalog", title: "Layout 9 — Visual catalog (premium feel)", desc: "Photo grid of each treatment. Boutique-clinic vibe — but requires you to source 30+ photos.", component: LayoutVisualCatalog },
  { key: "flatalpha", title: "Layout 10 — Flat alphabetical list", desc: "Single scrollable list, A→Z. No categories. Quickest to scan if you remember the treatment name; long if 30+ items.", component: LayoutFlatAlpha },
  { key: "flatprice", title: "Layout 11 — Flat list sorted by price (low → high)", desc: "Same flat list but sorted cheapest first. Great hook for price-sensitive patients — Free consults float to the top.", component: LayoutFlatPrice },
  { key: "flatsearch", title: "Layout 12 — Search bar + flat list (no categories)", desc: "Search field on top, full alphabetical list below. Type to filter instantly. Cleanest possible UX if patients know what they want.", component: LayoutFlatSearch },
  { key: "flattwocol", title: "Layout 13 — Two-column dense list", desc: "Desktop shows two columns side-by-side so the whole menu fits without scrolling; mobile collapses to one column. Fast visual scan.", component: LayoutFlatTwoCol },
  { key: "flatmenu", title: "Layout 14 — Numbered 'Treatment Menu' (restaurant style)", desc: "Numbered list with dotted price-line — like a high-end restaurant menu. Distinctive boutique feel, looks expensive in a good way.", component: LayoutFlatMenu },
];

export default function ServicePickerPreview() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.services().then(setServices).catch(() => setServices([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8">
          <span className="inline-block px-2 py-0.5 rounded-md bg-[#FFE9E0] text-[#C85A41] text-[11px] font-bold uppercase tracking-wider mb-2">Internal preview — not linked from anywhere</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1A1A1A] mb-2" style={{ fontFamily: "Manrope", letterSpacing: "-0.02em" }}>Service picker — 14 layouts</h1>
          <p className="text-[#5C5C5C] max-w-2xl">Same {services.length} services your patients see. Layouts 1-9 are categorised, layouts 10-14 are flat lists (no categories). Try each on desktop AND mobile, then tell me the layout key you want wired into the live flow.</p>
        </div>

        <div className="space-y-8">
          {LAYOUTS.map(({ key, title, desc, component: Layout }) => (
            <section key={key} className="ws-card p-5 sm:p-8">
              <h2 className="text-xl font-extrabold text-[#1A1A1A] mb-1" style={{ fontFamily: "Manrope" }}>{title}</h2>
              <p className="text-sm text-[#5C5C5C] mb-5">{desc}</p>
              {services.length === 0 ? (
                <p className="text-sm text-[#9CA3AF]">Loading services…</p>
              ) : (
                <Layout services={services} />
              )}
            </section>
          ))}
        </div>

        <div className="mt-12 text-center text-sm text-[#5C5C5C]">
          Like one? Tell the AI which layout key (accordion / search / hero / twostep) you&apos;d like in the live flow.
        </div>
      </div>
    </div>
  );
}
